import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { TbChevronLeft, TbCash, TbFileText, TbMoneybag } from 'react-icons/tb';
import { FaChevronDown } from 'react-icons/fa';
import PageHeader from '../../components/misc/PageHeader';
import {
  BalanceStats,
  CreateBankTransactionModel,
  Currency,
  JournalEntryModel,
  JOURNAL_ENTRY_SOURCE_TYPES,
  LedgerAccount,
  LedgerAccountType,
  ContributionPlanWithPayments,
  ContributionPaymentWithTransactionDetails,
} from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import { formatDate } from '../../../utils/stringUtils';
import { formatBalance, formatBalanceWithSign, transactionToJournalEntryDto, calculateMemberWalletImpact, formatEuroCents } from '../../../utils/bankAccountUtil';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import Button from '../../components/buttons/Button';
import OptionsButton from '../../components/buttons/OptionsButton';
import { OptionsButtonOption } from '../../../types/misc';
import TransactionProcessor from '../../components/financial/TransactionProcessor';
import Avatar from '../../components/misc/Avatar';
import ProgressBar from '../../components/misc/ProgressBar';
import DeclarationModal from '../../components/modals/DeclarationModal';
import ModalLayout from '../../components/layouts/ModalLayout';
import Labeled from '../../components/misc/Labeled';
import CurrencyInput from '../../components/forms/CurrencyInput';
import connectionService from '../../../services/community/connectionService';

export default function FinancialMemberInformationPage() {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.memberInformation' });
  const { t: tPersonal, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.memberInformation.personal',
  });
  const { t: tAdd } = useTranslation('', { keyPrefix: 'page.financial.add' });
  const { t: tDeclarations } = useTranslation('', {
    keyPrefix: 'page.financial.memberInformation.declarations',
  });
  const navigate = useNavigate();
  const params = useParams();
  const dashboardId = useSelector((state: RootState) => state.application.selectedDashboard);
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER,
  );
  const allConnections = useSelector((state: RootState) => state.connections);
  const connectionsMap = allConnections.reduce((acc, conn) => {
    acc[conn.dashboardMemberId] = conn;
    return acc;
  }, {} as Record<number, Connection>);

  const [journalEntries, setJournalEntries] = useState<JournalEntryModel[] | null>();
  const [contributionBalances, setContributionBalances] = useState<Record<
    number,
    BalanceStats
  > | null>();
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency] = useState<Currency>(Currency.EUR);
  const [initialBalanceInCents, setInitialBalanceInCents] = useState<number>(0);
  const [editBalanceModalOpen, setEditBalanceModalOpen] = useState(false);
  const [createDeclarationModalOpen, setCreateDeclarationModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ContributionPlanWithPayments | null>(null);
  const [contributionPlanModalOpen, setContributionPlanModalOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);

  const dashboardMemberId = params.dashboardMemberId ? +params.dashboardMemberId : null;
  const connection = connections.find((c) => +c.dashboardMemberId === dashboardMemberId!);

  const loadData = () => {
    if (!dashboardMemberId) return;
    setLoading(true);
    Promise.all([
      bankAccountService.getAllJournalEntries(),
      bankAccountService.getContributionBalancesForConnections(),
      bankAccountService.getMemberWalletBalance(dashboardMemberId, selectedCurrency).catch(() => ({ balance: 0, currency: selectedCurrency })),
    ])
      .then(([entries, bs, wallet]) => {
        setJournalEntries(entries);
        setContributionBalances(bs);
        setWalletBalance(wallet.balance);
        setLoading(false);
      })
      .catch(() => {
        setJournalEntries(null);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!dashboardMemberId) {
      navigate('/financial/reports');
      return;
    }
    loadData();
  }, [dashboardMemberId, navigate]);

  useEffect(() => {
    if (connection) {
      setInitialBalanceInCents(connection.financeAdministrationStartAmount || 0);
    }
  }, [connection?.financeAdministrationStartAmount]);

  // Calculate member-specific data
  const memberData = useMemo(() => {
    if (!journalEntries || !contributionBalances || !dashboardMemberId || walletBalance === null) return null;

    // Personal journal entries: member has participant lines OR entry is PERSONAL_* for this member (dashboardMemberId)
    // Also include CONTRIBUTION_RUN entries for this member
    const personalEntries = journalEntries.filter((entry) => {
      const hasParticipantLine = entry.lines?.some((line) => line.participantMemberId === dashboardMemberId);
      if (hasParticipantLine) return true;
      const isPersonalForMember =
        (entry.dashboardMemberId === dashboardMemberId || entry.payerId === dashboardMemberId) &&
        (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
          entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE);
      const isContributionForMember =
        entry.dashboardMemberId === dashboardMemberId &&
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;
      const isSavingPotForMember =
        (entry.dashboardMemberId === dashboardMemberId || entry.lines?.some((line) => line.participantMemberId === dashboardMemberId)) &&
        (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_FUND ||
          entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.SAVING_POT_RELEASE);
      return !!isPersonalForMember || !!isContributionForMember || !!isSavingPotForMember;
    });

    // Compute income/expenses from personal entries (member's share per entry)
    let personalIncome = 0;
    let personalExpenses = 0;
    personalEntries.forEach((entry) => {
      // Use calculateMemberWalletImpact to get correct wallet impact for the member
      const amount = calculateMemberWalletImpact(entry, dashboardMemberId);
      
      if (amount > 0) personalIncome += amount;
      else personalExpenses += Math.abs(amount);
    });

    const personalStats = {
      income: personalIncome,
      expenses: personalExpenses,
      balance: walletBalance,
    };

    // Contribution entries

    // Sales entries (placeholder for future implementation)

    // Declarations: only GROUP_EXPENSE with payerId (split/group “paid for”); exclude personal income/expense

    // Contribution entries - filter by dashboardMemberId matching the member

    return {
      stats: personalStats,
      transactions: personalEntries,
    };
  }, [journalEntries, contributionBalances, dashboardMemberId, walletBalance]);

  const getPaymentAmount = useMemo(() => {
    return (payment: ContributionPaymentWithTransactionDetails): number => {
      return payment.journalEntry?.lines?.reduce((sum: number, line: any) => sum + line.debit, 0) || 0;
    };
  }, []);

  const handleSaveInitialBalance = async (newAmountInCents: number) => {
    if (!connection) return;
    connectionService.updateInitialBalance(connection.dashboardMemberId, newAmountInCents, selectedCurrency)
      .then(() => {
        toast.success(tPersonal('modal.editInitialBalance.success'));
        loadData();
      })
      .catch((error) => {
        console.error('Error updating initial balance:', error);
        toast.error(tPersonal('modal.editInitialBalance.error'));
      });
  };

  const handleContributionEntryClick = async (entry: JournalEntryModel) => {
    if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && entry.sourceId) {
      setLoadingPlan(true);
      try {
        const plan = await bankAccountService.getContributionPlanWithPayments(entry.sourceId);
        setSelectedPlan(plan);
        setContributionPlanModalOpen(true);
      } catch (error) {
        console.error('Error loading contribution plan:', error);
        toast.error('Failed to load contribution plan');
      } finally {
        setLoadingPlan(false);
      }
    }
  };

  if (!dashboardMemberId || !connection) {
    toast.error(t('error.memberNotFound'));
    navigate('/financial/reports');
    return null;
  }

  if (loading) {
    return <CgSpinner size={150} className="mx-auto mt-20 animate-spin text-primary-300" />;
  }

  if (!memberData) {
    toast.error(t('error.dataNotFound'));
    navigate('/financial/reports');
    return null;
  }

  const addOptions: OptionsButtonOption[] = [
    {
      icon: TbFileText,
      name: tAdd('declaration') || 'Add Declaration',
      onClick: () => setCreateDeclarationModalOpen(true),
    },
    {
      icon: TbMoneybag,
      name: tPersonal('modal.editInitialBalance.title', { memberName: '' }) || 'Set Initial Balance',
      onClick: () => setEditBalanceModalOpen(true),
    },
  ];

  return (
    <>
      <Link to="/financial/member-balance">
        <Button className="mb-2 gap-1 p-0">
          <TbChevronLeft size={20} /> {t('back')}
        </Button>
      </Link>

      <PageHeader
        title={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
        subtitle={t('subtitle')}
        badge={connection.type === ConnectionType.EXTERNAL_MEMBER ? t('badgeForExternalMemberWarning') : undefined}
        primaryButton={
          <OptionsButton
            options={addOptions}
            containerClassName="left-0 sm:right-0 sm:left-auto"
            className="bg-primary text-secondary-50 flex h-fit w-fit items-center gap-2 whitespace-nowrap rounded-[12px] px-[16px] py-[8px] font-[600] transition-all hover:bg-opacity-80 hover:text-opacity-80">
            <FaChevronDown className="h-5 w-5" />
            {tAdd('add') || 'Add'}
          </OptionsButton>
        }
      />

      <div className="w-full">
        {/* Stats Tiles */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div
            className="cursor-pointer rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-primary-300 hover:shadow-md"
            onClick={() => setEditBalanceModalOpen(true)}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.initialBalance')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBalanceWithSign(initialBalanceInCents, selectedCurrency, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-8 w-8 text-success" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.balance')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBalanceWithSign(memberData.stats.balance, selectedCurrency, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-8 w-8 text-primary-300" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.income')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBalance(memberData.stats.income, selectedCurrency, i18n.language)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TbCash className="h-8 w-8 text-danger" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.expenses')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBalance(memberData.stats.expenses, selectedCurrency, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        {memberData.transactions.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            {tPersonal('noTransactions')}
          </div>
        ) : (
          <FinancialTable
            columns={[
              { id: 'date', label: tPersonal('table.date') },
              { id: 'description', label: tPersonal('table.description') },
              { id: 'amount', label: tPersonal('table.amount') },
              { id: 'type', label: tPersonal('table.type') },
              { id: 'processedBy', label: tPersonal('table.processedBy') },
            ]}
            rows={memberData.transactions}
            renderRow={(entry: JournalEntryModel) => {
              // Use calculateMemberWalletImpact to get correct wallet impact for the member
              const memberNetAmount = calculateMemberWalletImpact(entry, dashboardMemberId!);
              const isContribution = entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && entry.sourceId;

              return (
                <>
                  <FinancialDataCell 
                    onClick={isContribution ? () => handleContributionEntryClick(entry) : undefined}
                    className={isContribution ? 'cursor-pointer' : ''}
                  >
                    {formatDate(entry.entryDate)}
                  </FinancialDataCell>
                  <FinancialDataCell 
                    onClick={isContribution ? () => handleContributionEntryClick(entry) : undefined}
                    className={isContribution ? 'cursor-pointer' : ''}
                  >
                    {entry.description}
                  </FinancialDataCell>
                  <FinancialDataCell 
                    onClick={isContribution ? () => handleContributionEntryClick(entry) : undefined}
                    className={isContribution ? 'cursor-pointer' : ''}
                  >
                    {formatBalanceWithSign(memberNetAmount, selectedCurrency, i18n.language)}
                  </FinancialDataCell>
                  <FinancialDataCell 
                    onClick={isContribution ? () => handleContributionEntryClick(entry) : undefined}
                    className={isContribution ? 'cursor-pointer' : ''}
                  >
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${memberNetAmount > 0
                        ? 'bg-green-100 text-success'
                        : 'bg-red-100 text-danger'
                        }`}>
                      {memberNetAmount > 0
                        ? tPersonal('table.income')
                        : tPersonal('table.expense')}
                    </span>
                  </FinancialDataCell>
                  <FinancialDataCell>
                    <TransactionProcessor entry={entry} />
                  </FinancialDataCell>
                </>
              );
            }}
          />
        )}
      </div>

      {/* Modals */}
      <EditInitialBalanceModal
        open={editBalanceModalOpen}
        setOpen={setEditBalanceModalOpen}
        connection={connection!}
        onSave={handleSaveInitialBalance}
      />

      <DeclarationModal
        open={createDeclarationModalOpen}
        setOpen={setCreateDeclarationModalOpen}
        initialTransaction={
          {
            amount: 0,
            description: '',
            date: new Date(),
            payerId: dashboardMemberId!,
          } as CreateBankTransactionModel
        }
        onSplitSuccess={(declaration: CreateBankTransactionModel) => {
          if (!dashboardId) {
            toast.error(tDeclarations('error.createFailed') || 'Failed to create declaration');
            return;
          }
          bankAccountService
            .getWalletAccounts()
            .then((accounts: LedgerAccount[]) => {
              const walletAccount = accounts.find(
                (acc) => acc.accountType === LedgerAccountType.ASSET && acc.dashboardMemberId === null,
              );
              const expenseAccount = accounts.find(
                (acc) => acc.accountType === LedgerAccountType.EXPENSE,
              );
              const incomeAccount = accounts.find(
                (acc) => acc.accountType === LedgerAccountType.REVENUE,
              );
              
              const dto = transactionToJournalEntryDto(
                declaration,
                expenseAccount?.ledgerAccountId,
                incomeAccount?.ledgerAccountId,
                walletAccount?.ledgerAccountId,
                Currency.EUR,
              );
              
              return bankAccountService.createJournalEntry({
                ...dto,
                dashboardId,
              });
            })
            .then(() => {
              toast.success(tDeclarations('success.created') || 'Declaration created successfully');
              setCreateDeclarationModalOpen(false);
              loadData();
            })
            .catch((error) => {
              const errorMessage = error?.message || tDeclarations('error.createFailed') || 'Failed to create declaration';
              toast.error(errorMessage);
              console.error('Failed to create declaration:', error);
            });
        }}
      />

      {selectedPlan && (
        <ContributionPlanDetailModal
          plan={selectedPlan}
          open={contributionPlanModalOpen}
          setOpen={(open) => {
            setContributionPlanModalOpen(open);
            if (!open) {
              setTimeout(() => setSelectedPlan(null), 200);
            }
          }}
          connections={connectionsMap}
          getPaymentAmount={getPaymentAmount}
          dashboardMemberId={dashboardMemberId!}
        />
      )}

      {loadingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <CgSpinner size={50} className="animate-spin text-primary-300" />
        </div>
      )}
    </>
  );
}

// ContributionPlanDetailModal Component
type ContributionPlanDetailModalProps = {
  plan: ContributionPlanWithPayments;
  open: boolean;
  setOpen: (open: boolean) => void;
  connections: Record<number, Connection>;
  getPaymentAmount: (payment: ContributionPaymentWithTransactionDetails) => number;
  dashboardMemberId: number;
};

function ContributionPlanDetailModal({
  plan,
  open,
  setOpen,
  connections,
  getPaymentAmount,
  dashboardMemberId,
}: ContributionPlanDetailModalProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  
  // Filter payments to only show payments for this specific member
  const memberPayments = plan.contributionPayments.filter(
    (payment) => payment.dashboardMemberId === dashboardMemberId
  );
  
  // Calculate paid amount for this member only
  const paidAmount = memberPayments.reduce(
    (sum, payment) => sum + getPaymentAmount(payment),
    0,
  );
  
  // Progress is based on member's individual payments vs plan amount (what 1 person should pay)
  const progress = plan.amount > 0 ? (paidAmount / plan.amount) * 100 : 0;
  const remainingAmount = plan.amount - paidAmount;

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-4xl" closeButton>
      <div className="flex max-h-[calc(100vh-5rem)] flex-col gap-6 overflow-y-auto rounded-md bg-secondary-50 p-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{plan.name}</h2>
          {plan.description && (
            <p className="mt-1 text-sm text-gray-600">{plan.description}</p>
          )}
        </div>

        {/* Plan Details */}
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-secondary-200 bg-white p-4">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('table.amount')}</p>
            <p className="mt-1 text-lg font-semibold">{formatEuroCents(plan.amount, i18n.language)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('table.installments')}</p>
            <p className="mt-1 text-lg font-semibold">{plan.numberOfInstallments}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('createModal.frequency')}</p>
            <p className="mt-1 text-sm">{plan.frequency}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('createModal.startDate')}</p>
            <p className="mt-1 text-sm">{formatDate(new Date(plan.startDate))}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">{t('table.paid')}</p>
            <p className="text-sm font-semibold">
              {formatEuroCents(paidAmount, i18n.language)} / {formatEuroCents(plan.amount, i18n.language)}
            </p>
          </div>
          <ProgressBar progress={progress} variant="primary-light" showText={false} />
          <div className="mt-2 flex justify-between text-xs text-gray-600">
            <span>{progress.toFixed(0)}% {t('completed') || 'completed'}</span>
            <span>
              {formatEuroCents(remainingAmount, i18n.language)} {t('remaining') || 'remaining'}
            </span>
          </div>
        </div>

        {/* Members */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gray-700">{t('table.assignedTo')}</p>
          <div className="flex flex-wrap gap-2">
            {plan.appliesTo.map(({ dashboardMemberId: memberId }) => {
              const connection = connections[memberId];
              if (!connection) return null;
              return (
                <div
                  key={memberId}
                  className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                  <Avatar
                    className="h-6 w-6 rounded-full"
                    src={connection.picture}
                    alias={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
                  />
                  <span className="text-sm">
                    {connection.fields.FIRST_NAME} {connection.fields.LAST_NAME}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment History */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gray-700">
            {t('paymentHistory') || 'Payment History'}
          </p>
          {memberPayments.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {t('noPayments') || 'No payments yet'}
            </p>
          ) : (
            <div className="space-y-2">
              {memberPayments.map((payment) => {
                const connection = connections[payment.dashboardMemberId];
                const amount = getPaymentAmount(payment);
                const paymentDate = payment.journalEntry?.entryDate
                  ? formatDate(new Date(payment.journalEntry.entryDate))
                  : '-';

                return (
                  <div
                    key={payment.contributionPaymentId}
                    className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center gap-3">
                      {connection && (
                        <Avatar
                          className="h-8 w-8 rounded-full"
                          src={connection.picture}
                          alias={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
                        />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {connection
                            ? `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`
                            : `Member ${payment.dashboardMemberId}`}
                        </p>
                        <p className="text-xs text-gray-500">{paymentDate}</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold">{formatEuroCents(amount, i18n.language)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}

interface EditInitialBalanceModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  connection: Connection;
  onSave: (newAmount: number) => void;
}

function EditInitialBalanceModal({
  open,
  setOpen,
  connection,
  onSave,
}: EditInitialBalanceModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.memberInformation.personal' });
  const [amountInEuros, setAmountInEuros] = useState<number>(0);
  const [isPositive, setIsPositive] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      // Convert cents to euros when modal opens
      const initialAmount = (connection.financeAdministrationStartAmount || 0) / 100;
      setAmountInEuros(Math.abs(initialAmount));
      setIsPositive(initialAmount >= 0);
    }
  }, [open, connection.financeAdministrationStartAmount]);

  const handleSave = () => {
    const finalAmount = isPositive ? amountInEuros : -amountInEuros;
    onSave(Math.round(finalAmount * 100));
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-md rounded-lg" closeButton>
      <div className="rounded-md bg-secondary-50 p-6">
        <h2 className="mb-4 text-xl font-semibold">
          {t('modal.editInitialBalance.title', { memberName: `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}` })}
        </h2>

        <div className="mb-6 space-y-4">
          {/* Type Toggle Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsPositive(true)}
              className={`flex-1 rounded-lg border-2 px-4 py-3 font-medium transition-all ${isPositive
                  ? 'border-success bg-success text-white shadow-md'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-success hover:bg-green-50'
                }`}>
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">+</span>
                <span>{t('modal.editInitialBalance.credit')}</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setIsPositive(false)}
              className={`flex-1 rounded-lg border-2 px-4 py-3 font-medium transition-all ${!isPositive
                  ? 'border-danger bg-danger text-white shadow-md'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-danger hover:bg-red-50'
                }`}>
              <div className="flex items-center justify-center gap-2">
                <span className="text-lg">−</span>
                <span>{t('modal.editInitialBalance.debt')}</span>
              </div>
            </button>
          </div>

          {/* Amount Input */}
          <Labeled label={t('modal.editInitialBalance.initialBalanceLabel')}>
            <div className="relative">
              <CurrencyInput
                type="text"
                className={`w-full ${isPositive
                    ? 'border-success focus:border-success focus:ring-success'
                    : 'border-danger focus:border-danger focus:ring-danger'
                  }`}
                autoFocus
                handleChange={(value) => setAmountInEuros(Math.abs(value))}
                value={amountInEuros.toFixed(2)}
                debounced
              />
              <div
                className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-2xl font-bold ${isPositive ? 'text-success' : 'text-danger'
                  }`}>
                {isPositive ? '+' : '−'}
              </div>
            </div>
          </Labeled>

          {/* Preview */}
          <div className={`rounded-lg border-2 p-4 text-center ${isPositive
              ? 'border-success bg-green-50'
              : 'border-danger bg-red-50'
            }`}>
            <p className="text-sm font-medium text-gray-600">
              {t('modal.editInitialBalance.preview')}
            </p>
            <p className={`text-3xl font-bold ${isPositive ? 'text-success' : 'text-danger'
              }`}>
              {isPositive ? '+' : '−'}€{amountInEuros.toFixed(2)}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              {isPositive
                ? t('modal.editInitialBalance.memberHasCredit')
                : t('modal.editInitialBalance.memberHasDebt')
              }
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="primary-dark"
            className="bg-secondary-200 text-primary-900"
            onClick={handleCancel}>
            {t('modal.editInitialBalance.cancel')}
          </Button>
          <Button variant="primary-dark" onClick={handleSave}>
            {t('modal.editInitialBalance.save')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}